import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { GetAllClaimSetsPayload } from '@/domains/claimSets/store/types/AllClaimsState';
import type { AllClaimSetsListResponse } from '@/domains/claimSets/types/api/responses/AllClaimSetsListResponse';
import type { EDSAdapterServiceNormalizeConfiguration } from '@/features/eds/services/types/EDSAdapterServiceInjectionConfig';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { RootState } from '@/store/types/RootState';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { AllClaimSetsSlots } from '@/domains/claimSets/enums/AllClaimSetsSlots';
import {
  getAllClaimSetsList,
  getAllClaimSetsListSuccess,
  incrementAllClaimSetsListPage,
} from '@/domains/claimSets/store/allClaimSetsListSlice';
import { updateCustomInboxCounter } from '@/domains/user/store/userSlice';
import { MyClaimsTabs } from '@/enums/MyClaimsTabs';
import HttpService from '@/services/HttpService/HttpService';
import QueryParamsService from '@/services/QueryParamsService/QueryParamsService';

function* getAllClaimSetsListSaga({ type, payload }: PayloadAction<GetAllClaimSetsPayload>) {
  yield delay(100);
  try {
    const pagination = yield select((state: RootState) => state.allClaimSetsListSlice.pagination);
    const requestUrl = `/filter/${EdsEntityCodes.ClaimSet}?${QueryParamsService.constructSearchParamsFromRecord({ page: pagination.page, perPage: pagination.perPage, sortBy: payload?.sorting?.propertyName, sortDirection: payload?.sorting?.sortDirection })}`;

    const { data }: HttpServiceResponse<AllClaimSetsListResponse> = yield call(
      HttpService.post,
      requestUrl,
      payload?.filters || [],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizeConfig: Partial<EDSAdapterServiceNormalizeConfiguration> = {};

    if (payload.section) normalizeConfig.section = payload.section;

    const normalizedResponse: NormalizedV2Response[] = yield call(
      EdsAdapterService.normalize,
      data.listings,
      normalizeConfig,
    ) as Partial<EDSAdapterServiceNormalizeConfiguration>;

    EdsDebugService.logResponse(EdsEntityCodes.ClaimSet, {
      section: payload?.section,
      rawResponse: data,
      normalizedResponse,
      dataBySlot: normalizedResponse.length
        ? EdsAdapterService.generateSlotRecord<AllClaimSetsSlots>(normalizedResponse[0])
        : {},
    });

    const counters: { myClaimSets?: number; allClaimSets?: number } = {};
    if (payload.type === MyClaimsTabs.AllClaimSets) counters.allClaimSets = data.pagination.numberOfItems;
    else if (payload.type === MyClaimsTabs.MyClaimSets) counters.myClaimSets = data.pagination.numberOfItems;
    else if (payload?.location && payload?.customInboxId)
      yield put(
        updateCustomInboxCounter({
          id: payload.customInboxId,
          location: payload.location,
          counter: data.pagination.numberOfItems,
        }),
      );

    yield put(
      getAllClaimSetsListSuccess({
        shouldRewrite: type === getAllClaimSetsList.type,
        counters,
        listings: normalizedResponse,
        pagination: {
          ...data.pagination,
          isLastPage: data.pagination.page * data.pagination.perPage >= data.pagination.numberOfItems,
        },
      }),
    );
  } catch (error) {
    console.error(error);
  }
}

export function* allClaimSetsWatcher() {
  yield takeLatest([getAllClaimSetsList.type, incrementAllClaimSetsListPage.type], getAllClaimSetsListSaga);
}
